import { makeAutoObservable, runInAction } from 'mobx';
import { TimelinePositionManager } from './TimelinePositionManager';
import { TimelineMode } from '../commands/TimelineMode';
import { Framerate } from '../models/Timeline';

/**
 * Store for managing timeline-specific state and operations
 */
export class TimelineStore {
  // UI state
  private _timelineScale: number = 100; // pixels per second
  private _defaultTimelineScale: number = 100;
  private readonly _minTimelineScale: number = 10;
  private readonly _maxTimelineScale: number = 400;
  
  // Track display state
  private _trackHeights: Record<string, number> = {};
  
  // Timeline editing mode
  private _timelineMode: TimelineMode = TimelineMode.DEFAULT;
  
  // Drag state
  private _isPlayheadDragging: boolean = false;
  
  // Timeline position manager
  private _positionManager: TimelinePositionManager | null = null;
  
  // Private callbacks and refs
  private readonly duration: number;
  private readonly containerRef: React.RefObject<HTMLDivElement>;
  private readonly setTimeCallback: (time: number, updateRenderer: boolean) => void;
  private readonly setTimelineModeCallback: (mode: TimelineMode) => void;
  private readonly getFramerateCallback: () => Framerate;
  private readonly setFramerateCallback: (fps: Framerate) => void;
  private readonly getSnapToFrameCallback: () => boolean;
  private readonly setSnapToFrameCallback: (snap: boolean) => void;

  constructor(
    duration: number,
    containerRef: React.RefObject<HTMLDivElement>,
    setTimeCallback: (time: number, updateRenderer: boolean) => void,
    setTimelineModeCallback: (mode: TimelineMode) => void,
    getFramerateCallback: () => Framerate,
    setFramerateCallback: (fps: Framerate) => void,
    getSnapToFrameCallback: () => boolean,
    setSnapToFrameCallback: (snap: boolean) => void
  ) {
    this.duration = duration;
    this.containerRef = containerRef;
    this.setTimeCallback = setTimeCallback;
    this.setTimelineModeCallback = setTimelineModeCallback;
    this.getFramerateCallback = getFramerateCallback;
    this.setFramerateCallback = setFramerateCallback;
    this.getSnapToFrameCallback = getSnapToFrameCallback;
    this.setSnapToFrameCallback = setSnapToFrameCallback;

    makeAutoObservable(this, {}, { autoBind: true });
    
    // Initialize position manager
    this._positionManager = new TimelinePositionManager(
      this._timelineScale,
      this.duration,
      this.containerRef
    );
  }
  
  /**
   * Updates the timeline position manager when scale or duration changes
   */
  private updatePositionManager(): void {
    this._positionManager = new TimelinePositionManager(
      this._timelineScale,
      this.duration,
      this.containerRef
    );
  }
  
  /**
   * Get the current timeline scale
   */
  get timelineScale(): number {
    return this._timelineScale;
  }
  
  /**
   * Set the timeline scale (zoom level)
   */
  setTimelineScale(scale: number): void {
    runInAction(() => {
      this._timelineScale = Math.max(
        this._minTimelineScale,
        Math.min(this._maxTimelineScale, scale)
      );
      this.updatePositionManager();
    });
  }
  
  /**
   * Get the default timeline scale
   */
  get defaultTimelineScale(): number {
    return this._defaultTimelineScale;
  }
  
  /**
   * Zoom in the timeline
   */
  zoomIn(): void {
    this.setTimelineScale(this._timelineScale * 1.2);
  }
  
  /**
   * Zoom out the timeline
   */
  zoomOut(): void {
    this.setTimelineScale(this._timelineScale / 1.2);
  }
  
  /**
   * Calculate default timeline scale based on timeline duration and container width
   */
  calculateDefaultScale(containerWidth: number): void {
    if (this.duration && containerWidth) {
      const scale = (containerWidth - 30) / this.duration;
      runInAction(() => {
        this._defaultTimelineScale = scale;
        this._timelineScale = 0.95 * scale;
        this.updatePositionManager();
      });
    }
  }
  
  /**
   * Get the position manager
   */
  get positionManager(): TimelinePositionManager | null {
    return this._positionManager;
  }
  
  /**
   * Handle timeline click to position playhead
   */
  handleTimelineClick(e: React.MouseEvent<HTMLDivElement>): void {
    if (this._isPlayheadDragging) return;
    if (!this._positionManager) return;
    
    const time = this._positionManager.clientXToTimePosition(e.clientX);
    if (time !== null) {
      console.log(`Timeline clicked, moving to ${time.toFixed(2)}s`);
      this.setTimeCallback(time, true);
    }
  }
  
  /**
   * Set playhead dragging state
   */
  setPlayheadDragging(isDragging: boolean): void {
    runInAction(() => {
      this._isPlayheadDragging = isDragging;
    });
  }
  
  /**
   * Get playhead dragging state
   */
  get isPlayheadDragging(): boolean {
    return this._isPlayheadDragging;
  }
  
  /**
   * Set track height for a specific track
   */
  setTrackHeight(trackId: string, height: number): void {
    runInAction(() => {
      this._trackHeights[trackId] = height;
    });
  }
  
  /**
   * Get the height for a track (with defaults based on type)
   */
  getTrackHeight(trackId: string, type: string): number {
    if (this._trackHeights[trackId]) {
      return this._trackHeights[trackId];
    }
    
    // Default heights based on track type
    switch (type) {
      case 'video':
        return 70;
      case 'text':
        return 40;
      case 'audio':
        return 50;
      default:
        return 60;
    }
  }
  
  /**
   * Get the timeline mode
   */
  get timelineMode(): TimelineMode {
    return this._timelineMode;
  }
  
  /**
   * Set the timeline mode
   */
  setTimelineMode(mode: TimelineMode): void {
    runInAction(() => {
      this._timelineMode = mode;
      this.setTimelineModeCallback(mode);
    });
  }
  
  /**
   * Get the framerate
   */
  getFramerate(): Framerate {
    return this.getFramerateCallback();
  }
  
  /**
   * Set the framerate
   */
  setFramerate(fps: Framerate): void {
    this.setFramerateCallback(fps);
  }
  
  /**
   * Get the snap-to-frame setting
   */
  getSnapToFrame(): boolean {
    return this.getSnapToFrameCallback();
  }
  
  /**
   * Set the snap-to-frame setting
   */
  setSnapToFrame(snapToFrame: boolean): void {
    this.setSnapToFrameCallback(snapToFrame);
  }
}